import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useFlowium } from '../client/FlowiumContext';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';

interface PasswordFormProps {
    token: string;
    isAuthenticated: boolean;
    onSuccess: () => void;
}

const PasswordForm: React.FC<PasswordFormProps> = ({ token, isAuthenticated, onSuccess }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { client } = useFlowium();

    const handlePasswordChange = async () => {
        if (newPassword !== confirmNewPassword) {
            setError('New passwords do not match');
            return;
        }

        if (isAuthenticated && newPassword === currentPassword) {
            setError('New password must be different from the current password');
            return;
        }

        setLoading(true);

        try {
            if (isAuthenticated) {
                await client.changePassword(currentPassword, newPassword);
            } else {
                await client.resetPassword(token, newPassword);
            }
            setError('');
            onSuccess();
        } catch (error) {
            setError('Error changing password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-surface-inverse p-6 rounded-lg shadow-lg w-96">
            {isAuthenticated && (
                <input
                    type="password"
                    placeholder="Current Password"
                    aria-label="Current Password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full p-3 mb-4 border border-content-tertiary rounded-lg bg-transparent text-content-inverse placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-content-inverse"
                />
            )}
            <input
                type="password"
                placeholder="New Password"
                aria-label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-3 mb-4 border border-content-tertiary rounded-lg bg-transparent text-content-inverse placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-content-inverse"
            />
            <input
                type="password"
                placeholder="Confirm New Password"
                aria-label="Confirm New Password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="w-full p-3 mb-4 border border-content-tertiary rounded-lg bg-transparent text-content-inverse placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-content-inverse"
            />
            {error && <p className="pt-2 text-error text-sm">{error}</p>}
            {loading && <div className="my-4"><LoadingSpinner /></div>}
            <div className="flex justify-end space-x-2 mt-4">
                {isAuthenticated && (
                    <Button onClick={() => navigate('/settings/account')} variant="secondary">
                        Cancel
                    </Button>
                )}
                <Button onClick={handlePasswordChange} disabled={loading}>
                    Save
                </Button>
            </div>
        </div>
    );
};

export default PasswordForm;
